import type { ObraDetail, ReportSummary } from '@obras/shared';
import {
  daysBetween,
  formatCoordinate,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDays,
  formatPercent,
  ratio,
  REPORT_TYPE_META,
} from '@obras/shared';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  ArchiveRestore,
  ArrowLeft,
  Building2,
  CalendarClock,
  ChartLine,
  Expand,
  Eye,
  EyeOff,
  FileText,
  Hammer,
  HardHat,
  Images,
  MapPin,
  Paperclip,
  PauseCircle,
  Pencil,
  Plus,
  Trash2,
  type LucideIcon,
  UserCog,
} from 'lucide-react';
import { AnimatePresence, motion, type Variants } from 'motion/react';
import { Fragment, useState, type FormEvent, type ReactNode } from 'react';

import { EASE_OUT_QUINT, TRANSITION } from '../components/motion.js';
import { Badge, ObraStatusBadge, VisitStatusBadge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { Card, CardHeader, Kpi } from '../components/ui/Card.js';
import { CoverImage } from '../components/ui/Cover.js';
import { Avatar, ErrorState, ProgressBar, Skeleton } from '../components/ui/Feedback.js';
import { Lightbox, useLightbox, type LightboxItem } from '../components/ui/Lightbox.js';
import { Modal } from '../components/ui/Modal.js';
import { Moldura } from '../components/ui/Moldura.js';
import { Tab, TabList } from '../components/ui/Tabs.js';
import { ObraFilesTab } from '../features/documentos/ObraFilesTab.js';
import { ObraTeamTab } from '../features/obras/ObraTeamTab.js';
import { ObraFormModal } from '../features/obras/ObraFormModal.js';
import { useDeleteObra, useObra } from '../features/obras/api.js';
import { ObraFinanceTab, ObraGalleryTab, ObraStoppagesTab } from '../features/reports/ObraTabs.js';
import { ReportFormModal } from '../features/reports/ReportFormModal.js';
import { ReportTimeline } from '../features/reports/ReportTimeline.js';
import { useRemoveReport, useReports, useRestoreReport } from '../features/reports/api.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';
import { TextAreaField } from '../components/ui/Field.js';

const ICON = { strokeWidth: 1.5 } as const;

type TabId =
  'visao' | 'financeiro' | 'relatorios' | 'galeria' | 'paralisacoes' | 'equipe' | 'documentos';

const TABS: readonly { value: TabId; label: string; icon: LucideIcon; phase?: string }[] = [
  { value: 'visao', label: 'Visão geral', icon: HardHat },
  { value: 'financeiro', label: 'Financeiro', icon: ChartLine },
  { value: 'relatorios', label: 'Relatórios', icon: FileText },
  { value: 'galeria', label: 'Galeria', icon: Images },
  { value: 'paralisacoes', label: 'Paralisações', icon: PauseCircle },
  { value: 'equipe', label: 'Equipe', icon: UserCog },
  { value: 'documentos', label: 'Arquivos', icon: Paperclip },
];

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-[7px]">
      <span className="text-[13px] text-ink-3">{label}</span>
      <span className="tabular text-right text-[13px] font-medium">{value}</span>
    </div>
  );
}

/** Bloco lateral: título curto e conteúdo, sem o peso de um CardHeader. Quadrado. */
function SideCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <Card square className="p-5">
      <p className="mb-3 flex items-center gap-2 text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
        <Icon {...ICON} size={14} aria-hidden />
        {title}
      </p>
      {children}
    </Card>
  );
}

/** O nome entra em fila, palavra por palavra. */
const TITULO: Variants = {
  hidden: {},
  shown: { transition: { staggerChildren: 0.045, delayChildren: 0.12 } },
};

/** Cada palavra sobe de trás da fresta da própria caixa e assenta. */
const PALAVRA: Variants = {
  hidden: { y: '105%', opacity: 0 },
  shown: { y: '0%', opacity: 1, transition: { duration: 0.6, ease: EASE_OUT_QUINT } },
};

/** A faixa das informações chega depois do nome, subindo um fio; o texto dela, item a item. */
const INFORMACOES: Variants = {
  hidden: { opacity: 0, y: 6 },
  shown: {
    opacity: 1,
    y: 0,
    transition: { ...TRANSITION.base, delay: 0.38, delayChildren: 0.44, staggerChildren: 0.05 },
  },
};

const INFORMACAO: Variants = {
  hidden: { opacity: 0 },
  shown: { opacity: 1, transition: TRANSITION.base },
};

/**
 * Banner da obra.
 *
 * A foto da obra é o que identifica o lugar mais rápido que qualquer texto —
 * o fiscal reconhece o canteiro antes de ler o nome. Sobre ela vai uma tampa
 * chapada, não um gradiente: o degradê depende de onde o assunto da foto está,
 * e foto de campo não tem composição previsível.
 *
 * Quadrado, como o cartão de obra, e com os selos do mesmo jeito: cheios,
 * encostados no canto superior esquerdo, lado a lado e sem vão — a situação, a
 * reforma e a visita. A foto se revela ao chegar, com o visor de câmera enquanto
 * baixa (`<CoverImage>`).
 *
 * O nome entra palavra por palavra, cada uma subindo de trás de uma fresta, e as
 * informações de baixo vêm logo depois, numa faixa contínua de fundo preto a 25%
 * — legíveis sobre qualquer foto, clara ou escura.
 */
function ObraBanner({
  obra,
  onOpenPhoto,
}: {
  obra: ObraDetail;
  onOpenPhoto?: (() => void) | undefined;
}) {
  const palavras = obra.name.split(/\s+/).filter(Boolean);
  const informacoes = [
    obra.contractNumber
      ? { chave: 'contrato', texto: `Contrato ${obra.contractNumber}`, mono: true }
      : { chave: 'contrato', texto: 'Sem nº de contrato', mono: false },
    ...(obra.company
      ? [
          {
            chave: 'empresa',
            texto: obra.company.tradeName ?? obra.company.legalName,
            mono: false,
          },
        ]
      : []),
    ...(obra.city
      ? [
          {
            chave: 'municipio',
            texto: `${obra.city}${obra.state ? `/${obra.state}` : ''}`,
            mono: false,
          },
        ]
      : []),
  ];

  return (
    <div className="group relative h-[250px] overflow-hidden bg-rail sm:h-[340px]">
      {obra.coverUrl ? (
        <>
          <CoverImage src={obra.coverUrl} />
          <div className="absolute inset-0 bg-black/55" aria-hidden />
        </>
      ) : (
        <div className="absolute inset-0 grid place-items-center">
          <HardHat {...ICON} size={36} className="text-white/25" aria-hidden />
        </div>
      )}

      {/*
       * O botão cobre o banner inteiro, atrás do conteúdo, e o conteúdo não
       * captura ponteiro: clicar em qualquer ponto abre a foto, como na galeria.
       * Um <button> envolvendo o <h1> seria semanticamente errado.
       */}
      {onOpenPhoto ? (
        <>
          <button
            type="button"
            onClick={onOpenPhoto}
            aria-label="Ver foto da obra"
            className="absolute inset-0 z-0"
          />
          {/* No canto oposto ao dos selos, quadrado e preto a 65%, como o recolher do mapa. */}
          <span
            className="pointer-events-none absolute top-0 right-0 z-10 grid size-9 place-items-center bg-black/65 text-white opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden
          >
            <Expand size={16} strokeWidth={1.75} />
          </span>
        </>
      ) : null}

      {/*
       * Os selos encostam no canto superior esquerdo, lado a lado e sem respiro,
       * como no cartão de obra. A visita sai quando não se aplica: obra encerrada
       * não tem próxima visita, e um selo dizendo isso só ocuparia a foto.
       */}
      <span className="pointer-events-none absolute top-0 left-0 z-10 flex max-w-[calc(100%-2.25rem)] flex-wrap">
        <ObraStatusBadge status={obra.status} solid />
        {obra.isReform ? (
          <Badge tone="muted" icon={Hammer} solid>
            Reforma
          </Badge>
        ) : null}
        {obra.visitStatus !== 'nao_se_aplica' ? (
          <VisitStatusBadge status={obra.visitStatus} solid />
        ) : null}
      </span>

      <div className="pointer-events-none relative z-10 flex h-full flex-col justify-end p-5 sm:p-7">
        <div>
          {/*
           * Chaveado pelo nome: renomear a obra faz o título entrar de novo.
           * Cada palavra mora numa caixa que corta o que passa dela, e a folga
           * embaixo (com a margem negativa que a devolve) deixa as descendentes
           * — g, p, ç — inteiras.
           */}
          <motion.h1
            key={obra.name}
            variants={TITULO}
            initial="hidden"
            animate="shown"
            className="max-w-[40ch] text-[24px] leading-tight font-semibold tracking-tight text-white sm:text-[30px]"
          >
            {palavras.map((palavra, indice) => (
              <Fragment key={`${indice}-${palavra}`}>
                <span className="-mb-[0.15em] inline-block overflow-hidden pb-[0.15em] align-bottom">
                  <motion.span variants={PALAVRA} className="inline-block">
                    {palavra}
                  </motion.span>
                </span>
                {indice < palavras.length - 1 ? ' ' : null}
              </Fragment>
            ))}
          </motion.h1>

          <motion.ul
            key={`${obra.id}-informacoes`}
            aria-label="Informações da obra"
            variants={INFORMACOES}
            initial="hidden"
            animate="shown"
            className="mt-2.5 flex w-fit max-w-full flex-wrap items-center bg-black/25 px-2.5 py-1 text-[13px] leading-5 text-white/90"
          >
            {informacoes.map((informacao, indice) => (
              <motion.li
                key={informacao.chave}
                variants={INFORMACAO}
                className={cn(
                  informacao.mono && 'font-mono',
                  // O ponto entre uma informação e a seguinte, dentro da mesma faixa.
                  indice > 0 &&
                    "before:mx-2 before:font-sans before:text-white/50 before:content-['·']",
                )}
              >
                {informacao.texto}
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </div>
    </div>
  );
}

export function ObraDetailPage() {
  // `protected` é rota sem caminho (só guarda de sessão), mas compõe o id da filha.
  const { obraId } = useParams({ from: '/protected/obras/$obraId' });
  const navigate = useNavigate();
  const { can, may } = useAuth();

  const [tab, setTab] = useState<TabId>('visao');
  const [editOpen, setEditOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [verRemovidos, setVerRemovidos] = useState(false);
  const [removendo, setRemovendo] = useState<ReportSummary | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);
  const [restaurando, setRestaurando] = useState<ReportSummary | null>(null);
  const [erroDaRestauracao, setErroDaRestauracao] = useState<string | null>(null);
  /*
   * O relatório em correção fica guardado depois de fechar: o formulário ainda
   * sai da tela com o conteúdo dele, em vez de piscar o de um relatório novo.
   */
  const [corrigindo, setCorrigindo] = useState<ReportSummary | null>(null);
  const [correcaoAberta, setCorrecaoAberta] = useState(false);
  const lightbox = useLightbox();

  const { data: obra, isPending, isError, error, refetch } = useObra(obraId);

  /*
   * Quem corrige os relatórios da obra — o fiscal responsável, ou quem tem a
   * caixinha — o servidor diz (ADR 060); e quem corrige enxerga o que removeu,
   * como quem responde pelo histórico da obra. É a mesma regra do servidor,
   * repetida aqui só para não oferecer um interruptor que voltaria sem nada.
   */
  const podeCorrigir = obra?.viewerCanCorrectReports ?? false;
  const podeVerRemovidos = can('gestor') || may('canDeleteReports') || podeCorrigir;
  const reports = useReports({
    obraId,
    perPage: 100,
    includeDeleted: verRemovidos && podeVerRemovidos,
  });
  const remove = useDeleteObra();
  const removerRelatorio = useRemoveReport(obraId);
  const restaurarRelatorio = useRestoreReport(obraId);

  async function confirmarRestauracao(): Promise<void> {
    if (!restaurando) return;

    setErroDaRestauracao(null);

    try {
      await restaurarRelatorio.mutateAsync(restaurando.id);
      setRestaurando(null);
    } catch (erro) {
      setErroDaRestauracao(erro instanceof Error ? erro.message : 'Não foi possível restaurar.');
    }
  }

  async function confirmarRemocao(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!removendo) return;

    setErroDaRemocao(null);

    try {
      await removerRelatorio.mutateAsync({ reportId: removendo.id, reason: motivo.trim() });
      setRemovendo(null);
      setMotivo('');
    } catch (erro) {
      setErroDaRemocao(erro instanceof Error ? erro.message : 'Não foi possível remover.');
    }
  }

  if (isPending) {
    return (
      <>
        <Skeleton className="h-4 w-40" />
        {/* O lugar da moldura, sem ela: ela se desenha quando a obra chega. */}
        <div className="mt-4 p-1.5">
          <Skeleton className="h-[250px] rounded-none sm:h-[340px]" />
        </div>
        <Skeleton className="mt-5 h-10 w-full rounded-none" />
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-[121px] rounded-none" />
          ))}
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      </>
    );
  }

  const paidRatio = ratio(obra.paidValueCents, obra.currentValueCents);
  const contractDays =
    obra.contractStart && obra.contractEnd
      ? daysBetween(obra.contractStart, obra.contractEnd)
      : null;
  const osDays = obra.osStart && obra.osEnd ? daysBetween(obra.osStart, obra.osEnd) : null;

  const newReportButton = can('fiscal') ? (
    <Button
      variant="primary"
      icon={<Plus {...ICON} size={16} />}
      onClick={() => setReportOpen(true)}
    >
      Novo relatório
    </Button>
  ) : null;

  const remaining = obra.currentValueCents - obra.paidValueCents - obra.glossValueCents;

  /*
   * O banner abre o mesmo acervo da galeria, e não só a capa: quem clica na
   * foto quer ver a obra, não aquele arquivo específico. A capa costuma ser a
   * primeira foto do relatório mais recente, então normalmente já está na
   * lista; quando não está — capa enviada à parte — entra como primeiro slide.
   */
  const photos = (reports.data?.items ?? [])
    .flatMap((report) =>
      report.media
        .filter((item) => item.mime.startsWith('image/'))
        .map((item) => ({ ...item, reportTitle: report.title, visitDate: report.visitDate })),
    )
    .sort((a, b) => b.visitDate.localeCompare(a.visitDate));

  const slides: LightboxItem[] = photos.map((photo) => ({
    id: photo.id,
    url: photo.url,
    title: photo.reportTitle,
    meta: [
      formatDateTime(photo.takenAt) !== '—'
        ? formatDateTime(photo.takenAt)
        : formatDate(photo.visitDate),
      photo.latitude != null && photo.longitude != null
        ? `${formatCoordinate(photo.latitude, 'lat')} ${formatCoordinate(photo.longitude, 'lng')}`
        : null,
    ].filter((line): line is string => Boolean(line)),
  }));

  const coverIndex = obra.coverUrl ? slides.findIndex((slide) => slide.url === obra.coverUrl) : -1;

  const bannerSlides: LightboxItem[] =
    coverIndex >= 0 || !obra.coverUrl
      ? slides
      : [{ id: 'capa', url: obra.coverUrl, title: obra.name, meta: ['capa da obra'] }, ...slides];

  const bannerStart = coverIndex >= 0 ? coverIndex : 0;

  return (
    <>
      {/* Trilha e ações ficam fora do banner: botão sobre foto nunca tem contraste previsível. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <nav aria-label="Trilha" className="flex items-center gap-1.5 text-[13px]">
          <Link to="/obras" className="text-ink-3 transition-colors hover:text-ink">
            Obras
          </Link>
          <span className="text-ink-3" aria-hidden>
            ›
          </span>
          <span className="max-w-[40ch] truncate">{obra.name}</span>
        </nav>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            icon={<ArrowLeft {...ICON} size={16} />}
            onClick={() => void navigate({ to: '/obras' })}
          >
            Voltar
          </Button>
          {/*
           * O servidor diz se esta pessoa edita esta obra (ADR 052): a regra
           * depende da obra e do alcance da caixinha, e repeti-la aqui faria o
           * botão aparecer onde o `PATCH` recusa — era o que acontecia quando
           * ele seguia só o papel.
           */}
          {obra.viewerCanEdit ? (
            <Button icon={<Pencil {...ICON} size={16} />} onClick={() => setEditOpen(true)}>
              Editar
            </Button>
          ) : null}
          {can('admin') ? (
            <Button
              variant="danger-soft"
              icon={<Trash2 {...ICON} size={16} />}
              onClick={() => setDeleteOpen(true)}
            >
              Remover
            </Button>
          ) : null}
          {newReportButton}
        </div>
      </div>

      {/*
       * A moldura do banner, afastada 6px da foto. Desenha uma vez, quando a
       * obra chega — o esqueleto só reserva o lugar, para ela não se desenhar
       * duas vezes.
       */}
      <Moldura className="p-1.5">
        <ObraBanner
          obra={obra}
          onOpenPhoto={bannerSlides.length > 0 ? () => lightbox.open(bannerStart) : undefined}
        />
      </Moldura>

      <Lightbox
        items={bannerSlides}
        index={lightbox.index}
        onClose={lightbox.close}
        onNavigate={lightbox.navigate}
      />

      <div className="my-5">
        <TabList label="Seções da obra">
          {TABS.map((item) => {
            const count =
              item.value === 'relatorios'
                ? reports.data?.total
                : item.value === 'galeria'
                  ? reports.data?.items.reduce(
                      (sum, report) =>
                        sum + report.media.filter((m) => m.mime.startsWith('image/')).length,
                      0,
                    )
                  : undefined;

            return (
              <Tab
                key={item.value}
                active={tab === item.value}
                onClick={() => setTab(item.value)}
                icon={item.icon}
                disabled={Boolean(item.phase)}
                {...(item.phase ? { title: `Disponível na ${item.phase}` } : {})}
                {...(count ? { count } : {})}
              >
                {item.label}
              </Tab>
            );
          })}
        </TabList>
      </div>

      {/*
       * O painel da aba tem, no mínimo, a altura da janela menos as abas e o
       * respiro abaixo delas. Sem isso, trocar para uma aba curta encolhia a
       * página, e o navegador puxava a rolagem para cima — as abas saltavam do
       * alto da tela para o meio. Com o piso, as abas ficam onde estão e só o
       * conteúdo troca.
       *
       * A troca é em dois tempos curtos: o conteúdo anterior se apaga (100ms) e
       * o novo sobe 6px aparecendo (240ms). O piso fica fora da troca, para a
       * altura não cair no meio dela.
       */}
      <div
        role="tabpanel"
        aria-label={TABS.find((item) => item.value === tab)?.label}
        className="min-h-[calc(100dvh-3.75rem)]"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0, transition: TRANSITION.base }}
            exit={{ opacity: 0, transition: TRANSITION.instant }}
          >
            {tab === 'visao' ? (
              <div className="flex flex-col gap-3">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Kpi
                    label="Valor atual"
                    value={formatCurrency(obra.currentValueCents)}
                    hint={
                      obra.currentValueCents === obra.initialValueCents
                        ? 'sem aditivo ou supressão'
                        : `inicial ${formatCurrency(obra.initialValueCents)}`
                    }
                  />
                  <Kpi
                    label="Executado"
                    value={formatPercent(paidRatio)}
                    hint={formatCurrency(obra.paidValueCents)}
                    tone="success"
                  />
                  <Kpi
                    label="Saldo a executar"
                    value={formatCurrency(Math.max(remaining, 0))}
                    hint={
                      obra.glossValueCents > 0
                        ? `${formatCurrency(obra.glossValueCents)} em glosa`
                        : undefined
                    }
                  />
                  <Kpi
                    label="Próxima visita"
                    value={obra.nextVisitDueAt ? formatDate(obra.nextVisitDueAt) : '—'}
                    hint={`a cada ${formatDays(obra.visitIntervalDays)}`}
                    tone="ink"
                  />
                </div>

                <div className="grid gap-3 lg:grid-cols-3">
                  <div className="flex flex-col gap-3 lg:col-span-2">
                    <Card square className="p-5">
                      <h2 className="text-[15px] font-semibold tracking-tight">Objeto</h2>
                      <p className="mt-2 max-w-[68ch] leading-relaxed whitespace-pre-line text-ink-3">
                        {obra.description ?? 'Nenhuma descrição registrada para esta obra.'}
                      </p>
                    </Card>

                    <Card square>
                      <CardHeader
                        title="Contrato e Ordem de Serviço"
                        description="Vigência do contrato e prazo de execução são contados separadamente."
                      />
                      <div className="grid gap-x-8 px-5 pb-3 sm:grid-cols-2">
                        <div>
                          <p className="mb-1 text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
                            Contrato
                          </p>
                          <DataRow label="Número" value={obra.contractNumber ?? '—'} />
                          <DataRow label="Início" value={formatDate(obra.contractStart)} />
                          <DataRow label="Fim" value={formatDate(obra.contractEnd)} />
                          <DataRow
                            label="Duração"
                            value={contractDays !== null ? formatDays(contractDays) : '—'}
                          />
                        </div>

                        {/* Empilhado no celular, o segundo bloco precisa de respiro próprio. */}
                        <div className="mt-4 sm:mt-0">
                          <p className="mb-1 text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
                            Ordem de serviço
                          </p>
                          <DataRow label="Início" value={formatDate(obra.osStart)} />
                          <DataRow label="Fim" value={formatDate(obra.osEnd)} />
                          <DataRow
                            label="Duração"
                            value={osDays !== null ? formatDays(osDays) : '—'}
                          />
                        </div>
                      </div>

                      <div className="px-5 pb-5">
                        <p className="mb-2 text-[13px] text-ink-3">Prazo de contrato decorrido</p>
                        <ProgressBar
                          value={obra.timeProgress}
                          label="Prazo decorrido"
                          tone={obra.timeProgress >= 1 ? 'danger' : 'neutral'}
                        />
                      </div>
                    </Card>
                  </div>

                  <div className="flex flex-col gap-3">
                    <SideCard title="Empresa contratada" icon={Building2}>
                      {obra.company ? (
                        <div>
                          <p className="text-[14px] font-medium">
                            {obra.company.tradeName ?? obra.company.legalName}
                          </p>
                          {obra.company.tradeName ? (
                            <p className="mt-0.5 text-[13px] text-ink-3">
                              {obra.company.legalName}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <p className="text-[13px] text-ink-3">Nenhuma empresa vinculada.</p>
                      )}
                    </SideCard>

                    <SideCard title="Fiscalização" icon={UserCog}>
                      {obra.fiscais.length === 0 ? (
                        <p className="text-[13px] text-ink-3">Nenhum fiscal designado.</p>
                      ) : (
                        <ul className="flex flex-col gap-2.5">
                          {obra.fiscais.map((fiscal) => (
                            <li key={fiscal.id} className="flex items-center gap-2.5">
                              <Avatar name={fiscal.name} url={fiscal.avatarUrl} size={28} />
                              <span className="min-w-0 flex-1 truncate text-[13px]">
                                {fiscal.name}
                              </span>
                              {fiscal.role === 'responsavel' ? (
                                <Badge tone="primary">Responsável</Badge>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </SideCard>

                    <SideCard title="Localização" icon={MapPin}>
                      {obra.latitude != null && obra.longitude != null ? (
                        <div>
                          <p className="font-mono text-[13px]">
                            {formatCoordinate(obra.latitude, 'lat')}{' '}
                            {formatCoordinate(obra.longitude, 'lng')}
                          </p>
                          <p className="mt-1 text-[13px] text-ink-3">
                            {[obra.address, obra.city, obra.state].filter(Boolean).join(' · ') ||
                              'Endereço não informado'}
                          </p>
                          <a
                            href={`https://www.openstreetmap.org/?mlat=${obra.latitude}&mlon=${obra.longitude}#map=17/${obra.latitude}/${obra.longitude}`}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="mt-2 inline-block text-[13px] font-medium text-primary-ink transition-colors hover:text-primary"
                          >
                            Abrir no mapa
                          </a>
                        </div>
                      ) : (
                        <p className="text-[13px] text-ink-3">
                          Coordenadas não informadas. O mapa do portfólio depende delas.
                        </p>
                      )}
                    </SideCard>

                    {/* Sem repetir a próxima visita: ela já é um dos indicadores acima. */}
                    <SideCard title="Acompanhamento" icon={CalendarClock}>
                      <DataRow label="Última visita" value={formatDate(obra.lastVisitAt)} />
                      <DataRow
                        label="Intervalo de visita"
                        value={formatDays(obra.visitIntervalDays)}
                      />
                      <DataRow label="Relatórios" value={String(reports.data?.total ?? 0)} />
                      <DataRow
                        label="Paralisações"
                        value={obra.status === 'paralisada' ? 'em vigor' : '—'}
                      />
                    </SideCard>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === 'financeiro' ? <ObraFinanceTab obra={obra} /> : null}

            {/* Sem repetir "Novo relatório": o botão já está no topo da página. */}
            {tab === 'relatorios' ? (
              <>
                {/*
            O interruptor só aparece para quem pode ver removidos, e fica
            discreto: o normal é o histórico do que aconteceu, e conferir
            remoção é visita ocasional.
          */}
                {podeVerRemovidos ? (
                  <div className="mb-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setVerRemovidos((antes) => !antes)}
                      aria-pressed={verRemovidos}
                      className={cn(
                        'flex items-center gap-2 px-2.5 py-1.5 text-[13px] transition-colors',
                        verRemovidos
                          ? 'bg-sunken text-ink'
                          : 'text-ink-3 hover:bg-sunken hover:text-ink',
                      )}
                    >
                      {verRemovidos ? (
                        <Eye {...ICON} size={15} aria-hidden />
                      ) : (
                        <EyeOff {...ICON} size={15} aria-hidden />
                      )}
                      {verRemovidos ? 'Ocultar removidos' : 'Mostrar removidos'}
                    </button>
                  </div>
                ) : null}

                <ReportTimeline
                  reports={reports.data?.items ?? []}
                  total={reports.data?.total}
                  loading={reports.isPending}
                  emptyAction={newReportButton}
                  {...(podeCorrigir
                    ? {
                        onEdit: (report: ReportSummary) => {
                          setCorrigindo(report);
                          setCorrecaoAberta(true);
                        },
                        onRemove: (report: ReportSummary) => setRemovendo(report),
                        onRestore: (report: ReportSummary) => setRestaurando(report),
                      }
                    : {})}
                />
              </>
            ) : null}

            {tab === 'galeria' ? <ObraGalleryTab reports={reports.data?.items ?? []} /> : null}

            {tab === 'paralisacoes' ? <ObraStoppagesTab obra={obra} /> : null}

            {tab === 'equipe' ? <ObraTeamTab obra={obra} /> : null}

            {tab === 'documentos' ? <ObraFilesTab obraId={obra.id} /> : null}
          </motion.div>
        </AnimatePresence>
      </div>

      <ObraFormModal open={editOpen} onClose={() => setEditOpen(false)} obra={obra} />
      {/*
       * Um formulário por obra (`key`): o rascunho do relatório novo é lido na
       * montagem, e trocar de obra sem sair da página levaria o da anterior. A
       * correção só monta com o relatório em mãos — sem ele, seria um segundo
       * formulário de relatório novo, disputando o mesmo rascunho.
       */}
      <ReportFormModal
        key={obra.id}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        obra={obra}
      />
      {corrigindo ? (
        <ReportFormModal
          key={corrigindo.id}
          open={correcaoAberta}
          onClose={() => setCorrecaoAberta(false)}
          obra={obra}
          report={corrigindo}
        />
      ) : null}

      <Modal
        open={removendo !== null}
        onClose={() => {
          setRemovendo(null);
          setErroDaRemocao(null);
        }}
        title="Remover relatório"
        description="O registro sai do histórico, mas continua visível a quem corrige a obra, riscado."
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setRemovendo(null)}
              disabled={removerRelatorio.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              form="remover-relatorio"
              type="submit"
              loading={removerRelatorio.isPending}
              icon={<Trash2 {...ICON} size={16} />}
            >
              Remover relatório
            </Button>
          </>
        }
      >
        <form
          id="remover-relatorio"
          onSubmit={(event) => void confirmarRemocao(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          {erroDaRemocao ? (
            <div role="alert" className="bg-danger-soft px-3.5 py-3 text-[13px] text-danger">
              {erroDaRemocao}
            </div>
          ) : null}

          <p className="text-[13px] text-ink-3">
            <strong className="font-semibold text-ink">{removendo?.title}</strong>, de{' '}
            {formatDate(removendo?.visitDate ?? null)}.
          </p>

          {/* O relatório que moveu dinheiro leva o lançamento junto: o estorno sai no mesmo ato. */}
          {removendo?.movement && removendo.movement.reversedByMovementId === null ? (
            <p className="bg-sunken px-3.5 py-3 text-[13px] text-ink-3">
              {REPORT_TYPE_META[removendo.type].label} de{' '}
              <strong className="tabular font-semibold text-ink">
                {removendo.movement.days !== null
                  ? formatDays(Math.abs(removendo.movement.days))
                  : formatCurrency(Math.abs(removendo.movement.amountCents))}
              </strong>{' '}
              é estornada no livro-razão junto com a remoção. Se o relatório for restaurado, ela é
              lançada de novo.
            </p>
          ) : null}

          <TextAreaField
            label="Motivo da remoção"
            required
            rows={4}
            hint="Fica ao lado do relatório riscado e na auditoria. Mínimo de 10 caracteres."
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
          />
        </form>
      </Modal>

      <Modal
        open={restaurando !== null}
        onClose={() => {
          setRestaurando(null);
          setErroDaRestauracao(null);
        }}
        title="Restaurar relatório"
        description="O registro volta ao histórico da obra com os anexos, o prazo de visita é recalculado e o que a remoção estornou é lançado de novo."
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setRestaurando(null)}
              disabled={restaurarRelatorio.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={restaurarRelatorio.isPending}
              icon={<ArchiveRestore {...ICON} size={16} />}
              onClick={() => void confirmarRestauracao()}
            >
              Restaurar relatório
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {erroDaRestauracao ? (
            <div role="alert" className="bg-danger-soft px-3.5 py-3 text-[13px] text-danger">
              {erroDaRestauracao}
            </div>
          ) : null}

          <p className="text-[13px] text-ink-3">
            <strong className="font-semibold text-ink">{restaurando?.title}</strong>, de{' '}
            {formatDate(restaurando?.visitDate ?? null)}.
            {restaurando && restaurando.mediaCount > 0
              ? ` ${restaurando.mediaCount === 1 ? '1 anexo volta' : `${restaurando.mediaCount} anexos voltam`} a aparecer.`
              : ''}
          </p>
        </div>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Remover obra"
        description="A obra sai das listagens, mas o histórico e a auditoria são preservados."
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(false)}
              disabled={remove.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              icon={<Trash2 {...ICON} size={16} />}
              onClick={() => {
                remove.mutate(obra.id, {
                  onSuccess: () => void navigate({ to: '/obras' }),
                });
              }}
            >
              Remover obra
            </Button>
          </>
        }
      >
        <p className="text-ink-3">
          Confirma a remoção de <strong className="font-semibold">{obra.name}</strong>? A exclusão é
          lógica e pode ser revertida por um administrador diretamente no banco.
        </p>
      </Modal>
    </>
  );
}
